import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';

import type {
  WysiwygTableAction,
  WysiwygTableMenu,
} from 'admin-panel/utils/wysiwyg-editor/types';
import { TABLE_CONTROL_GAP } from 'admin-panel/utils/wysiwyg-editor/constants';

interface UiInputGroupWysiwygEditorTableControlsSignature {
  Element: HTMLDivElement;
  Args: {
    Named: {
      tableControlsStyle: ReturnType<typeof htmlSafe>;
      addTableRowBelow: (event: Event) => void;
      addTableColumnRight: (event: Event) => void;
      openTableMenu: (
        menu: Exclude<WysiwygTableMenu, null>,
        event: Event
      ) => void;
      closeTableMenu: () => void;
      closeTableMenuAndRemeasure: () => void;
      handleMenuFocusOut: (nextTarget: EventTarget | null) => void;
      showTableRowMenu: boolean;
      performTableAction: (action: WysiwygTableAction, event: Event) => void;
      canDeleteActiveTableRow: boolean;
      showTableColumnMenu: boolean;
      canDeleteActiveTableColumn: boolean;
    };
  };
}

export default class UiInputGroupWysiwygEditorTableControlsComponent extends Component<UiInputGroupWysiwygEditorTableControlsSignature> {
  private menuTriggers: Partial<
    Record<Exclude<WysiwygTableMenu, null>, HTMLButtonElement>
  > = {};
  private activeMenu?: Exclude<WysiwygTableMenu, null>;
  private menuListeners?: AbortController;

  readonly rowMenuId = `${guidFor(this)}-row-menu`;
  readonly columnMenuId = `${guidFor(this)}-column-menu`;

  get menuDestination() {
    return document.body;
  }

  @action
  registerMenuTrigger(menu: Exclude<WysiwygTableMenu, null>, element: Element) {
    if (element instanceof HTMLButtonElement) {
      this.menuTriggers[menu] = element;
    }
  }

  @action
  registerMenu(menu: Exclude<WysiwygTableMenu, null>, element: Element) {
    if (!(element instanceof HTMLDivElement)) {
      return;
    }

    this.activeMenu = menu;
    const trigger = this.menuTriggers[menu];

    if (!trigger) {
      return;
    }

    this.positionMenu(element, trigger);
    element
      .querySelector<HTMLButtonElement>('button:not(:disabled)')
      ?.focus({ preventScroll: true });
    this.menuListeners?.abort();
    this.menuListeners = new AbortController();
    const { signal } = this.menuListeners;
    let ancestor = trigger.parentElement;

    while (ancestor && ancestor !== document.body) {
      ancestor.addEventListener('scroll', this.closeMenuForLayoutChange, {
        signal,
      });
      ancestor = ancestor.parentElement;
    }

    window.addEventListener('scroll', this.closeMenuForLayoutChange, {
      signal,
    });
    window.addEventListener('resize', this.closeMenuForLayoutChange, {
      signal,
    });
  }

  @action
  unregisterMenu(menu: Exclude<WysiwygTableMenu, null>) {
    if (this.activeMenu !== menu) {
      return;
    }

    this.menuListeners?.abort();
    this.menuListeners = undefined;
    this.activeMenu = undefined;
  }

  @action
  handleMenuKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.closeMenuAndRestoreFocus();
  }

  @action
  handleMenuFocusOut(event: FocusEvent) {
    const menu = event.currentTarget;
    const nextTarget = event.relatedTarget;

    if (
      menu instanceof HTMLElement &&
      nextTarget instanceof Node &&
      menu.contains(nextTarget)
    ) {
      return;
    }

    if (this.activeMenu && nextTarget === this.menuTriggers[this.activeMenu]) {
      return;
    }

    this.args.handleMenuFocusOut(nextTarget);
  }

  @action
  closeMenuForLayoutChange() {
    this.closeMenuAndRestoreFocus(this.args.closeTableMenuAndRemeasure);
  }

  private closeMenuAndRestoreFocus(
    closeMenu: () => void = this.args.closeTableMenu
  ) {
    const trigger = this.activeMenu
      ? this.menuTriggers[this.activeMenu]
      : undefined;

    closeMenu();
    trigger?.focus({ preventScroll: true });
  }

  private positionMenu(element: HTMLDivElement, trigger: HTMLButtonElement) {
    const triggerRect = trigger.getBoundingClientRect();
    const preferredLeft =
      this.activeMenu === 'row'
        ? triggerRect.left
        : triggerRect.left + triggerRect.width / 2;
    const preferredTop = triggerRect.bottom + TABLE_CONTROL_GAP;

    element.style.left = `${preferredLeft}px`;
    element.style.top = `${preferredTop}px`;

    const menuRect = element.getBoundingClientRect();
    const viewportRight = window.innerWidth - TABLE_CONTROL_GAP;
    const viewportBottom = window.innerHeight - TABLE_CONTROL_GAP;
    const top =
      menuRect.bottom > viewportBottom
        ? Math.max(
            TABLE_CONTROL_GAP,
            triggerRect.top - menuRect.height - TABLE_CONTROL_GAP
          )
        : preferredTop;
    const menuLeft = Math.min(
      Math.max(menuRect.left, TABLE_CONTROL_GAP),
      viewportRight - menuRect.width
    );

    element.style.left = `${preferredLeft + menuLeft - menuRect.left}px`;
    element.style.top = `${top}px`;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::InputGroup::WysiwygEditor::TableControls': typeof UiInputGroupWysiwygEditorTableControlsComponent;
    'ui/input-group/wysiwyg-editor/table-controls': typeof UiInputGroupWysiwygEditorTableControlsComponent;
  }
}
